import * as React from "react";
import { AccordionQuestionnaireSection } from "@teamprevent/common/questionnaires/components/AccordionQuestionnaireSection";
import { DropdownQuestion } from "@teamprevent/common/questionnaires/components/questions/DropdownQuestion";

import { AnsweredQuestionWithId } from "@teamprevent/common/model";
import { TextQuestion } from "@teamprevent/common/questionnaires/components/questions/TextQuestion";
import { Message } from "semantic-ui-react";

import { calculatePepRiskAssessmentScore } from "../calculatePepRiskAssessmentScore";
import { PepRiskAssessmentQuestionsOptions, PercutaneousPenetrationAnswer } from "../models";
import { getAnswerToQuestionWithId } from "../../questionnaires/getAnswerToQuestionWithId";

export const PepRiskAssessmentSectionId = "PepRiskAssessment";
export const PepRecommendedQuestion = {
    id: "PepRecommended",
    question: ""
};

interface Props {
    questions: AnsweredQuestionWithId[];
    readonly: boolean;
    index?: number;
    questionnaireKey: string;
}

export const BbvPepRiskAssessmentSection: React.FunctionComponent<Props> = (props: Props) => {
    const natureOfExposureQuestionId = "PepNatureOfExposure";

    if (!props.readonly) {
        return null;
    }

    const sectionExists = getAnswerToQuestionWithId(props.questions, natureOfExposureQuestionId);
    if (!sectionExists) {
        return null;
    }

    return (
        <AccordionQuestionnaireSection
            id={PepRiskAssessmentSectionId}
            title="PEP Risk Assessment"
            index={props.index}
        >
            <DropdownQuestion
                mandatory
                readonly={props.readonly}
                questionConfig={{
                    id: natureOfExposureQuestionId,
                    questionText: "Nature of exposure"
                }}
                options={PepRiskAssessmentQuestionsOptions.PepNatureOfExposure.map(o => ({ value: o.value, text: o.text }))}
            />
            <DropdownQuestion
                mandatory
                readonly={props.readonly}
                questionConfig={{
                    id: "PepMaterialExposedTo",
                    questionText: "Material exposed to"
                }}
                options={PepRiskAssessmentQuestionsOptions.PepMaterialExposedTo.map(o => ({ value: o.value, text: o.text }))}
            />
            {getAnswerToQuestionWithId(props.questions, natureOfExposureQuestionId) === PercutaneousPenetrationAnswer && <>
                <DropdownQuestion
                    mandatory
                    readonly={props.readonly}
                    questionConfig={{
                        id: "PepTypeOfSharp",
                        questionText: "Type of sharp"
                    }}
                    options={PepRiskAssessmentQuestionsOptions.PepTypeOfSharp.map(o => ({ value: o.value, text: o.text }))}
                />
                <DropdownQuestion
                    mandatory
                    readonly={props.readonly}
                    questionConfig={{
                        id: "PepSharpsUsed",
                        questionText: "Sharps used"
                    }}
                    options={PepRiskAssessmentQuestionsOptions.PepSharpsUsed.map(o => ({ value: o.value, text: o.text }))}
                />
            </>}
            <DropdownQuestion
                mandatory
                readonly={props.readonly}
                questionConfig={{
                    id: "PepWoundDescription",
                    questionText: "Wound Description"
                }}
                options={PepRiskAssessmentQuestionsOptions.PepWoundDescription.map(o => ({ value: o.value, text: o.text }))}
            />
            <DropdownQuestion
                mandatory
                readonly={props.readonly}
                questionConfig={{
                    id: "PepRiskFactorsForHiv",
                    questionText: "Risk factors for HIV"
                }}
                options={PepRiskAssessmentQuestionsOptions.PepRiskFactorsForHiv.map(o => ({ value: o.value, text: o.text }))}
            />
            <Message info>
                {
                    props.readonly ?
                        <TextQuestion
                            readonly
                            questionConfig={{
                                id: PepRecommendedQuestion.id,
                                questionText: PepRecommendedQuestion.question
                            }}
                            hideDivider
                        /> :
                        <p>{calculatePepRiskAssessmentScore(props.questions) >= 10 ? "PEP Recommended" : "PEP Not Recommended"}</p>
                }
            </Message>
        </AccordionQuestionnaireSection>
    );
};
